const CURRENCY = "USD";

// package imports
import Head from "next/head";
import { observer } from "mobx-react-lite";
import dynamic from "next/dynamic";

//local imports
import { constructLink, convertEntityKeyToName } from "@/utils/linkConstructor";
import { findLocale } from "@/locales";
import { connectToCarDataDB } from "@/db/connectCarData";
import { fetchNearestAirportData, fetchStaticData } from "@/utils/staticDataFetch";
import { recursivelyEncodeShareURL } from "@/utils/utilFunctions";
import RentalPartners from "@/components/common/search-box/RentalPartners";
import { connectToDatabase } from "@/db/connectDb";

//components
import Footer from "@/components/layout/footer";
import SearchBox from "@/components/common/search-box";
import BreadCrumbs from "@/components/airport/bread-crumb";
import RentalFeatures from "@/components/airport/rental-features";
import {
  generateInnerAgencyLinks,
  generateInnerBrandLinks,
  generateInnerClassLinks,
  returnElectricCarCount,
} from "@/utils/generateInnerLinks";
import AllAgencyList from "@/components/airport/all-agencies";
import { getUniqueAgencies } from "@/utils/uniqueParse";
import InTerminalCars from "@/components/airport/in-terminal-cars";
import AvailableBrands from "@/components/common/car-brand-list";

// import axios from "axios";

type Props = {
  ctid: string;
  locationName: string;
  sidData: string;
  locale: string;
  resultsData: string;
  url: string;
  id: string;
  airport: any;
  nearestAirports: string;
  location: {
    lat: number;
    lng: number;
  };
  rating: {
    ratingValue: number;
    ratingCount: number;
    bestRating: number;
  };
  brandList: string[];
  airportData: AirportNameData;
  agencyList: string;
  classList: string[];
  hasElectricCars: boolean;
};

const DynamicAvailableClasses = dynamic(
  () => import("@/components/airport/available-classes"),
  {
    loading: () => <p>Loading...</p>,
  }
);

const DynamicRentServesYou = dynamic(
  () => import("@/components/common/rent80-serves-you"),
  {
    loading: () => <p>Loading...</p>,
  }
);

const DynamicRentalCompanyList = dynamic(
  () => import("@/components/airport/rental-company-list"),
  {
    loading: () => <p>Loading...</p>,
  }
);

const DynamicMaps = dynamic(() => import("@/components/airport/map"), {
  loading: () => <p>Loading...</p>,
});

const DynamicFAQ = dynamic(() => import("@/components/common/faq"), {
  loading: () => <p>Loading...</p>,
});

const DynamicDownloadApp = dynamic(
  () => import("@/components/common/download-app")
);

const DynamicNewsletter = dynamic(
  () => import("@/components/common/newsletter")
);

const DynamicCheapestCars = dynamic(
  () => import("@/components/airport/cheapest-cars")
);

const DynamicAirportsNearYou = dynamic(
  () => import("@/components/airport/near-you"),
  {
    loading: () => <p>Loading...</p>,
  }
);

const AirportPage = ({
  ctid,
  sidData,
  locationName,
  locale,
  resultsData,
  url,
  id,
  airport,
  nearestAirports,
  location,
  airportData,
  agencyList,
  classList,
  brandList,
  hasElectricCars,
  rating,
}: // airport,

  //FIXME: Changing the value in currency section in navbar does not update the currency in the page.
  Props) => {
  const agencyListData: Agency[] = JSON.parse(agencyList);
  const results = recursivelyEncodeShareURL(JSON.parse(resultsData), { undo: true }) as Result;
  const sid = sidData;
  const fullCarList = [
    ...results?.car_data,
    ...results?.recommended_cars,
    ...results?.most_popular_cars,
    ...results?.cheapest_cars,
  ].filter((car) => car?.car.brand !== "Class Special Car");

  //scan through the array and remove duplicate objects
  const carList: CarData[] = Array.from(
    new Set(fullCarList.map((car) => car?.id))
  )
    .map((carId) => fullCarList.find((car) => car?.id === carId))
    .filter((car): car is CarData => car !== undefined);
  // I hate how well TypeScript works sometimes
  // The final filter just ensures that the value of carList is never undefined

  const nearestAirportData = JSON.parse(nearestAirports);

  const productSchemaJSON = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: `${locationName.replace(/International/g, "Intl")} Car Rentals`,
    image: "https://www.rent80.com/og-image.png",
    description: `Car Rentals at ${locationName.replace(
      /International/g,
      "Intl"
    )}. Compare and Find Best Car rental deals in nearby Airports.`,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: rating.ratingValue,
      ratingCount: rating.ratingCount,
      bestRating: rating.bestRating,
    },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: CURRENCY,
      lowPrice: results?.lowestPrice,
      highPrice: Math.ceil(results?.cheapest_cars.slice(-1)[0]?.totalPrice),
      offerCount: carList.length,
    },
  };

  const breadcrumbSchemaJSON = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        item: {
          "@type": "Thing",
          "@id": "https://www.rent80.com",
          name: "Car Rentals",
        },
      },
      {
        "@type": "ListItem",
        position: 2,
        item: {
          "@type": "Thing",
          "@id": "https://www.rent80.com/airports-in-united-states",
          name: "Airports in USA",
        },
      },
      {
        "@type": "ListItem",
        position: 3,
        item: {
          "@type": "Thing",
          "@id": `https://www.rent80.com/${url}`,
          name: `Car Rentals at ${airportData.shortCode} Airport`,
        },
      },
    ],
  };

  return (
    <>
      <Head>
        <title>{`Car Rental at ${locationName.replace(
          /International/g,
          "Intl"
        )}`}</title>
        <meta
          name="description"
          content={`Car Rentals at ${locationName.replace(
            /International/g,
            "Intl"
          )}. Compare and Find Best Car rental deals in nearby Airports.`}
        />
        {typeof window !== "undefined" && (
          <>
            <script type="application/ld+json">
              {JSON.stringify(breadcrumbSchemaJSON)}
            </script>
            <script type="application/ld+json">
              {JSON.stringify(productSchemaJSON)}
            </script>
          </>
        )}
        <link rel="image_src" href="/og-image.png" />
        <meta property="og:image" content="https://www.rent80.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:title"
          content={`Car Rental at ${locationName.replace(
            /International/g,
            "Intl"
          )}`}
        />
        <meta
          property="og:description"
          content={`Car Rentals at ${locationName.replace(
            /International/g,
            "Intl"
          )}. Compare and Find Best Car rental deals in nearby Airports.`}
        />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Rent80" />

        <meta property="og:url" content={`https://www.rent80.com/${url}`} />
        {/* {
          locales.map(locale => (
            <link rel="alternate" hrefLang={locale} href={`https://www.rent80.com/${locale}/${url}`} key={locale} />
          ))
        }
        <link rel="alternate" href="https://www.rent80.com/${url}" hrefLang="x-default" /> */}
        <link rel="canonical" href={`https://www.rent80.com/${url}`} />
      </Head>
      <main className="bg-[#F1F2F7] min-h-screen w-full overflow-x-clip ">
        <SearchBox
          sid={sid}
          locale={locale}
          type={"airport"}
          locationName={locationName}
          ctid={ctid}
          airport={airport}
          airportData={airportData}
        />
        <BreadCrumbs location={locationName} />

        <RentalPartners
          type="agency"
          agencyData={getUniqueAgencies(carList)}
          locationName={locationName}
        />

        <DynamicCheapestCars
          airportData={airportData}
          name="Top"
          locationName={locationName}
          type="airport"
          results={results}
          locale={locale}
        />

        <DynamicNewsletter />

        <DynamicMaps
          airports={JSON.parse(resultsData)?.airportCoordinates ?? null}
          cardata={results ? results.car_data : undefined}
          name={locationName}
          location={location}
          airport={airport}
          airportData={airportData}
          locale={locale}
        />

        {agencyListData.length > 0 ? (
          <RentalFeatures
            result={results}
            name={locationName}
            agencyList={agencyListData}
            baseUrl={url}
          />
        ) : (
          <DynamicRentServesYou price="$5.99" />
        )}
        <DynamicRentalCompanyList
          baseUrl={url}
          agencyList={agencyListData}
          locationName={locationName}
          locale={locale}
          data={results}
        />
        <DynamicDownloadApp />

        <DynamicCheapestCars
          name="Cheapest"
          locationName={locationName}
          type="airport"
          results={results}
          locale={locale}
          airportData={airportData}
        />
        {nearestAirportData.length > 1 && (
          <div className="hidden sm:grid md:grid lg:grid">
            <DynamicAirportsNearYou
              airports={nearestAirportData}
              currentAirport={(locationName.match(/\[(.*?)\]/) || [])[1]}
              locationName={locationName}
            />
          </div>
        )}
        {brandList.length > 0 && (
          <AvailableBrands
            locationName={locationName}
            brandList={brandList}
            url={url}
          />
        )}
        {nearestAirportData[0]?.lat && nearestAirportData[0]?.lng &&
          <AllAgencyList
            locale={locale}
            locationName={locationName}
            airportData={{
              lat: nearestAirportData[0].lat,
              lng: nearestAirportData[0].lng,
            }}
            cardata={carList}
            airportNameData={airportData}
          />}
        {carList.filter((a) =>
          a?.agency.pickupLocation.locType === "IN_TERMINAL" ||
          a?.agency.pickupLocation.locCategory === "AT_AIRPORT"
        ).length > 0 && (
            <InTerminalCars
              locationName={locationName}
              updatedAt={results?.updatedAt}
              carData={carList}
            />
          )}

        <DynamicCheapestCars
          airportData={airportData}
          name="Most Booked"
          locationName={locationName}
          type="airport"
          results={results}
          locale={locale}
        />
        {(classList.length > 0 || hasElectricCars) && (
          <DynamicAvailableClasses
            locationName={locationName}
            classList={classList}
            url={url}
            hasElectricCars={hasElectricCars}
          />
        )}

        <DynamicFAQ
          type="airport"
          locationName={locationName}
          carName={
            results?.most_popular_cars[0]?.car?.brand ||
            "Standard Chevrolet Camaro Convertible"
          }
          lowestPrice={
            results?.lowestPrice
              ? `${findLocale(locale)?.currencySymbol}${results.lowestPrice}`
              : "$5.99"
          }
        />
        <Footer />
      </main>
    </>
  );
};

export default observer(AirportPage);

// Fetches the data for individual pages
export const getStaticProps = async ({
  params: { airport },
  locale,
}: {
  params: {
    airport: string;
  };
  locale: string;
}) => {
  const ctid = airport.split("-").pop();
  const ctidNum = parseInt(ctid!);
  if (Number.isNaN(ctidNum) || ctid !== ctidNum.toString()) {
    return { notFound: true };
  }

  const airportCode = airport.split("-").reverse()[2].toUpperCase();
  if (!/^[A-Z]{3}$/.test(airportCode)) {
    return { notFound: true };
  }
  //FIXME: This is a very jank method to fix the duplicate ctid issue. Find a permanent solution later.

  const currency = findLocale(locale)?.currency || CURRENCY;
  //FIXME: Find a method to fix the locale issue without having to completely switch the current implementation.

  // const { sidData, searchIdData, resultsData } = await fetchData(
  //   ctid,
  //   country,
  //   params?.airport.toString() || "unknown",
  //   currency
  // );

  const { carDB } = await connectToCarDataDB();


  const carData = await fetchStaticData(ctidNum, currency, airportCode);

  const foundAirport = await carDB
    .collection("destinations")
    .findOne({ ctid: ctidNum, kayakId: airportCode });

  if (!foundAirport) {
    return { notFound: true };
  }

  if (carData === null) {
    console.error("Error Fetching Data: ", airport, "(Invalid CTID and/or Currency combination)");
  } else {
    console.log("Generating Page:", airport);
  }

  const location = {
    lat: foundAirport.lat,
    lng: foundAirport.lng,
  };

  const countryCode = foundAirport.cc === "US" ? "USA" : foundAirport.cc;
  // TODO: Comment out the line after the country code formats are normalised in the DB

  const locationName = await `${convertEntityKeyToName(
    foundAirport.entityKey
  )} [${foundAirport.id}], ${countryCode}`;

  const airportLocation = await `${foundAirport.cityonly
    .replace(/\W+/g, "-")
    .toLowerCase()}_${foundAirport.region.replace(/\W+/g, "-").toLowerCase()}`;

  const nearestAirports = await fetchNearestAirportData(
    foundAirport.lat,
    foundAirport.lng
  );

  const airportData = {
    id: foundAirport.apicode,
    airportName: foundAirport.airportname,
    cityName: foundAirport.cityname,
    cityOnly: foundAirport.cityonly,
    country: foundAirport.country,
    countryCode: foundAirport.cc,
    regionCode: foundAirport.rc,
    region: foundAirport.region,
    shortCode: foundAirport.searchFormPrimary || "",
    shortName: foundAirport.searchFormSecondary || "",
    secondaryName: foundAirport.secondary || "",
    shortDisplayName: foundAirport.shortdisplayname || "",
    longName: foundAirport.smartyDisplay || "",
  };

  const agencyList: Agency[] = await generateInnerAgencyLinks(
    ctidNum.toString()
  );
  const classList = await generateInnerClassLinks(ctidNum.toString());
  const brandList = await generateInnerBrandLinks(ctidNum.toString());

  const electricCarCount = await returnElectricCarCount([ctidNum.toString()]);
  const hasElectricCars: boolean = electricCarCount?.[0]?.electricCarCount > 1;

  return {
    props: {
      airportData: airportData,
      ctid: ctid,
      nearestAirports: JSON.stringify(nearestAirports) || null,
      // sid: sidData || null,
      // searchId: searchIdData || null,
      location: location || null,
      locationName: locationName.replace(/([a-z])([A-Z])/g, "$1 $2") || null,
      resultsData: carData ? JSON.stringify(carData) : null,
      url: airport,
      locale,
      id: foundAirport.id || null,
      airport: airportLocation || null,
      agencyList: JSON.stringify(agencyList) || null,
      classList: classList || null,
      brandList: brandList || null,
      hasElectricCars: hasElectricCars || false,
      rating: {
        ratingValue: parseFloat((Math.random() * (9.8 - 7.2) + 7.2).toFixed(1)),
        ratingCount: Math.ceil(Math.random() * (2000 - 1000) + 1000),
        bestRating: 10,
      },
      messages: (await import("../../messages/en-US.json")).default,
    },
    // revalidate: 60 * 60 * 24, // 1 day
  };
};

// Geenrates the paths for the static pages
export async function getStaticPaths() {
  const params: any = [];
  const { carDB } = await connectToCarDataDB();
  const { db } = await connectToDatabase();
  //FIXME: Switch the DB to the carDB once the build issues are fixed.
  const locationsDataMongoFull = await db
    // .collection("destinations")
    .collection("destinations")
    .find({
      kayakType: "ap",
      cc: "US",
      entityKey: { $exists: true, $ne: null },
      country: "United States",
    })
    // You may need to adjust this number based on your data
    .toArray();

  const ctidArray = locationsDataMongoFull.map((item: any) =>
    item.ctid.toString()
  );

  const validCtidArr = await carDB
    .collection("parseddatas")
    .aggregate([
      {
        $match: {
          ctid: { $in: ctidArray },
          currency: CURRENCY,
        },
      },
      {
        $group: {
          _id: null,
          ctidArray: { $addToSet: "$ctid" },
        },
      },
    ])
    .toArray();

  const validCtid = validCtidArr[0].ctidArray;

  const locationsDataMongo = locationsDataMongoFull.filter(
    (item: any) =>
      ctidArray.includes(item.ctid.toString()) &&
      validCtid.includes(item.ctid.toString())
  );
  // let count = 0; // Initialize count of items that pass the if condition

  for (let item of locationsDataMongo) {
    // Check if item.shortdisplayname contains only English letters, numbers, and common punctuation
    let isEnglish = /^[a-zA-Z0-9.,!?'\-\(\) ]*$/.test(item.shortdisplayname);

    if ((item.entityKey || item.location) && isEnglish) {
      let locationName: string = constructLink(item);
      let url: string = `${locationName}`;

      item.id &&
        locationName &&
        params.push({
          params: {
            airport: url,
          },
        });

      // await db
      //     .collection("destinations")
      //     .updateOne({ _id: item._id }, { $set: item }, { upsert: true });

      // count += 1; // Increment count

      // // Break the loop once you've reached the limit
      // if (count >= 70) {
      //     break;
      // }
    }
  }
  console.log("Airports to Generate: ", params.length);

  return {
    paths: params,
    fallback: "blocking",
  };
}
